import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { useActiveProject } from '../../core/contexts/ProjectContext';
import { extractErrorMessage } from '../../core/services/api';
import { invitationsService } from '../../core/services/invitations.service';
import { ROLE_DESCRIPTION, ROLE_LABEL } from '../../core/services/permissions';
import { GoogleSignInButton } from '../../shared/components/GoogleSignInButton';
import type { InvitationPreview } from '../../core/types/data-models';

/**
 * Landing page for an emailed invitation link (`/invite?token=…`).
 *
 * Public route — the invitee may have no account at all. Signing in with Google
 * here creates their account, their own empty workspace (named from their Google
 * identity), and activates the grant, all in one call. They are never asked to
 * name a workspace.
 */
export function InviteAcceptPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, acceptInvitation, logout, loading } = useAuth();
  const { setActiveProject } = useActiveProject();

  const token = params.get('token') ?? '';
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoadError('This invitation link is missing its token.');
      setLoadingPreview(false);
      return;
    }
    let cancelled = false;
    invitationsService
      .preview(token)
      .then((data) => !cancelled && setPreview(data))
      .catch((err) => !cancelled && setLoadError(extractErrorMessage(err)))
      .finally(() => !cancelled && setLoadingPreview(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = useCallback(
    async (idToken?: string) => {
      setAcceptError(null);
      try {
        const result = await acceptInvitation(token, idToken);
        // The token is single-purpose and now spent — drop it from the URL so it
        // doesn't linger in browser history.
        setParams({}, { replace: true });
        setActiveProject({
          id: result.invitedProject.id,
          name: result.invitedProject.name,
          tenantId: result.invitedProject.tenantId,
          tenantName: result.invitedProject.tenantName,
          role: result.invitedProject.role,
          access: 'shared',
        } as never);
        navigate('/', { replace: true });
      } catch (err) {
        setAcceptError(extractErrorMessage(err));
      }
    },
    [acceptInvitation, navigate, setActiveProject, setParams, token],
  );

  if (loadingPreview) {
    return (
      <CenteredCard>
        <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Checking this invitation…
          </Typography>
        </Stack>
      </CenteredCard>
    );
  }

  // State 3/4 — expired or revoked. Distinguish the reasons; "invalid" is not a
  // useful thing to tell someone who was legitimately invited last week.
  const terminal = loadError
    ? loadError
    : preview?.status === 'EXPIRED'
      ? `This invitation expired${preview.expiresAt ? ` on ${new Date(preview.expiresAt).toLocaleDateString()}` : ''}. Ask ${preview.invitedByName} to send a new one.`
      : preview?.status === 'REVOKED'
        ? `This invitation was withdrawn. Ask ${preview.invitedByName} if you still need access.`
        : null;

  if (terminal || !preview) {
    return (
      <CenteredCard>
        <Stack spacing={2}>
          <Typography variant="h6">Invitation unavailable</Typography>
          <Alert severity="warning">{terminal ?? 'This invitation could not be loaded.'}</Alert>
          <Button variant="outlined" onClick={() => navigate('/login', { replace: true })}>
            Go to sign in
          </Button>
        </Stack>
      </CenteredCard>
    );
  }

  const signedInAsInvitee =
    isAuthenticated && user?.email?.toLowerCase() === preview.invitedEmail.toLowerCase();
  const signedInAsSomeoneElse = isAuthenticated && !signedInAsInvitee;

  return (
    <CenteredCard>
      <Stack spacing={3}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <GroupAddIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h5" fontWeight={700}>
            {preview.invitedByName} invited you
          </Typography>
          <Typography variant="body1">
            to <strong>{preview.projectName}</strong> in <strong>{preview.tenantName}</strong>
          </Typography>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={ROLE_LABEL[preview.role]}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {ROLE_DESCRIPTION[preview.role]}
          </Typography>
        </Stack>

        {acceptError && <Alert severity="error">{acceptError}</Alert>}

        {/* State 2 — already signed in as someone else. Never silently accept
            as the wrong account: the grant is bound to one address. */}
        {signedInAsSomeoneElse && (
          <Stack spacing={2}>
            <Alert severity="warning">
              This invitation is for <strong>{preview.invitedEmail}</strong>, but you are signed in
              as <strong>{user?.email}</strong>.
            </Alert>
            <Button variant="contained" onClick={logout}>
              Sign out and continue as {preview.invitedEmail}
            </Button>
          </Stack>
        )}

        {/* State 1 — already signed in as the invitee. One click, no re-auth. */}
        {signedInAsInvitee && (
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={() => void accept()}
          >
            {loading ? 'Accepting…' : 'Accept invitation'}
          </Button>
        )}

        {/* State 0 — signed out. Google sign-in creates the account, their own
            workspace, and activates the grant in a single call. */}
        {!isAuthenticated && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Sign in as <strong>{preview.invitedEmail}</strong> to accept.
            </Typography>
            <GoogleSignInButton
              text="continue_with"
              disabled={loading}
              onCredential={(idToken) => accept(idToken)}
            />
          </Stack>
        )}
      </Stack>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>{children}</Paper>
      </Container>
    </Box>
  );
}
